/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import Tables from "layouts/tables";
import Billing from "layouts/billing";
import RTL from "layouts/rtl";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";

// @mui icons
import Icon from "@mui/material/Icon";
import ProtectedRoute from "./components/Utils/ProtectedRoute";
import Audittrails from "./layouts/audittrails";
import Users from "./layouts/users";
import TransactionSummary from "./layouts/reports/TransactionSummary";
import TransactionReport from "./layouts/reports/TransactionReport";
import ResetPassword from "./layouts/resetpassword";
import Cover from "./layouts/authentication/reset-password/cover";
import ResetWithOtp from "./layouts/authentication/reset-password/reset-with-otp";
import Merchants from "./layouts/merchants";
import Single from "./layouts/merchants/single";
import ThirtyDaysActive from "./layouts/reports/30daysactive";
import CashTransactions from "./layouts/reports/CashTransactions";
import CardTransactions from "./layouts/reports/CardTransactions";
import SoftPosTransactionReport from "./layouts/reports/SoftPosTransactionReport";
import CardTransactionsDetailed from "./layouts/reports/CardTransactionsDetailed";
import DwhTransactions from "./layouts/reports/DwhTransactions";
import RefundTransactions from "./layouts/reports/RefundTransactions";
import VoidTransactions from "./layouts/reports/VoidTransactions";
import TrackEmail from "./layouts/track/TrackEmail";
import TrackSms from "./layouts/track/TrackSms";
import UserLevelParams from "./layouts/web-user-params/user-level-params";
import RolesConfiguration from "./layouts/web-user-params/roles-configuration";
import OnboardingFiles from "./layouts/reports/OnboardingFiles";
import CardSettlementSummary from "./layouts/reports/CardSettlementSummary";
import MerchantGroups from "./layouts/merchant-groups";
import MerchantGroupSingle from "./layouts/merchant-groups/single";
import NewMerchant from "./layouts/merchants/new";
import MdrMaster from "./layouts/masters/MdrMaster";
import MdrMasterChannels from "./layouts/masters/MdrMasterChannels";
import MdrMasterNew from "./layouts/masters/MdrMasterNew";
import CapkMaster from "./layouts/masters/CapkMaster";
import MdrDefaultMaster from "./layouts/masters/MdrDefaultMaster";

import FirstTime from "./layouts/authentication/firsttime-password/index";
import BinMaster from "./layouts/masters/BinMaster";
import PosTransactionReport from "./layouts/reports/PosTransactionReport";
import SettlementReport from "./layouts/reports/SettlementReport";
import CardSettlementReport from "./layouts/reports/CardSettlementReport";
import RoutingRules from "./layouts/routing-rules";
import ResetImei from "./layouts/resetimeipin/resetImei";
import ResetPin from "./layouts/resetimeipin/resetPin";
import MerchantUsers from "./layouts/merchants/tabs/users";
import MerchantAllUsers from "./layouts/merchants/MerchantAllUsers";
import MerchantAllTerminals from "./layouts/merchants/MerchantAllTerminals";
import MerchantStatement from "./layouts/reports/MerchantStatement";
import NetSettlementReport from "./layouts/reports/NetSettlementReport";
import GrossSettlementReport from "./layouts/reports/GrossSettlementReport";
import MonthlyNetSettlementReport from "./layouts/reports/MonthlyNetSettlementReport";

import DeFileReleaseScreen from "./layouts/reports/DeFileReleaseScreen";

import DwhFilesProccess from "./layouts/reports/DwhFilesProccess";
import DeReplyFile from "./layouts/reports/DeReplyFile";
import CnpTransactions from "./layouts/reports/CnpTransactions";
import GroupConfig from "./terminalconfigcomponents/groupconfig/groupconfig";
import Deviceconfig from "./terminalconfigcomponents/deviceconfig/deviceconfig";
import DeletedDevices from "./terminalconfigcomponents/deviceconfig/deleteddevices";
import AllControlConfig from "./terminalconfigcomponents/control-config/components/allcontrolconfig";
import Parameterconfig from "./terminalconfigcomponents/control-config/parameterconfig";
import DebitAccountConfig from "./layouts/debit-account-config";
import MerchantCategoryCodeConfig from "layouts/merchant-category-code";
import BankTLSConfig from "layouts/bank-tls-config";

const routes = [
  // {
  //   type: "single",
  //   name: "Dashboard",
  //   key: "dashboard",
  //   icon: <Icon fontSize="small">dashboard</Icon>,
  //   route: "/dashboard",
  //   component: <ProtectedRoute><Dashboard /></ProtectedRoute>,
  //   doShowInSidebar: true,
  //   module: "Dashboard",
  //   subModule: "Admin",
  // },
  {
    type: "collapse",
    name: "Web User Parameters",
    key: "web-user-parameters",
    icon: <Icon fontSize="small">web</Icon>,
    doShowInSidebar: true,
    collapse: [
      {
        type: "collapse",
        name: "User Level Parameters",
        key: "user-level-parameters",
        icon: <Icon fontSize="small">reports</Icon>,
        route: "/user-level-parameters",
        component: (
          <ProtectedRoute>
            <UserLevelParams />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
        module: "Role",
        subModule: "Browse",
      },
      {
        type: "collapse",
        name: "Roles Configuration",
        key: "roles-configuration",
        icon: <Icon fontSize="small">reports</Icon>,
        route: "/roles-configuration",
        component: (
          <ProtectedRoute>
            <RolesConfiguration />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
        module: "Permission",
        subModule: "Browse",
      },
    ],
  },
  // {
  //   type: "collapse",
  //   name: "Merchants",
  //   key: "merchant-for-admin",
  //   icon: <Icon fontSize="small">store</Icon>,
  //   doShowInSidebar: true,
  //   collapse: [
  //     {
  //       type: "collapse",
  //       name: "Onboarding files",
  //       key: "onboarding-files",
  //       icon: <Icon fontSize="small">reports</Icon>,
  //       route: "/onboarding-files",
  //       component: <ProtectedRoute><OnboardingFiles /></ProtectedRoute>,
  //       doShowInSidebar: true,
  //       module: "Merchants",
  //       subModule: "OboardFiles",
  //     },
  //     {
  //       type: "collapse",
  //       name: "Merchant Groups",
  //       key: "merchant-groups",
  //       icon: <Icon fontSize="small">store</Icon>,
  //       route: "/merchant-groups",
  //       component: <ProtectedRoute><MerchantGroups /></ProtectedRoute>,
  //       doShowInSidebar: true,
  //       module: "MerchantGroup",
  //       subModule: "Browse",
  //     },
  // {
  //   type: "collapse",
  //   name: "Merchants",
  //   key: "merchants",
  //   icon: <Icon fontSize="small">store</Icon>,
  //   route: "/merchants",
  //   component: (
  //     <ProtectedRoute>
  //       <Merchants />
  //     </ProtectedRoute>
  //   ),
  //   doShowInSidebar: true,
  //   // module: "Merchants",
  //   // subModule: "Browse",
  // },
  //     {
  //       type: "collapse",
  //       name: "Merchant Users",
  //       key: "merchant-users",
  //       icon: <Icon fontSize="small">store</Icon>,
  //       route: "/merchant-users",
  //       component: <ProtectedRoute><MerchantAllUsers /></ProtectedRoute>,
  //       doShowInSidebar: true,
  //       module: "User",
  //       subModule: "BrowseMerchantUser",
  //     },
  //     {
  //       type: "collapse",
  //       name: "Merchant Terminals",
  //       key: "merchant-terminals",
  //       icon: <Icon fontSize="small">store</Icon>,
  //       route: "/merchant-terminals",
  //       component: <ProtectedRoute><MerchantAllTerminals /></ProtectedRoute>,
  //       doShowInSidebar: true,
  //       module: "Terminal",
  //       subModule: "Browse",
  //     },
  //   ],
  // },
  // {
  //   type: "single",
  //   name: "Merchant",
  //   key: "merchant",
  //   icon: <Icon fontSize="small">store</Icon>,
  //   route: "/merchants/:mid",
  //   component: <ProtectedRoute><Single /></ProtectedRoute>,
  //   doShowInSidebar: false,
  //   module: "Merchants",
  //   subModule: "BrowseMerchant",
  // },
  // {
  //   type: "single",
  //   name: "New Merchant",
  //   key: "new_merchant",
  //   icon: <Icon fontSize="small">store</Icon>,
  //   route: "/merchants/new",
  //   component: <ProtectedRoute><NewMerchant /></ProtectedRoute>,
  //   doShowInSidebar: false,
  //   module: "Merchants",
  //   subModule: "Create",
  // },
  // {
  //   type: "single",
  //   name: "Merchant Groups",
  //   key: "merchant-groups",
  //   icon: <Icon fontSize="small">store</Icon>,
  //   route: "/merchant-group/single",
  //   component: <ProtectedRoute><MerchantGroupSingle /></ProtectedRoute>,
  //   doShowInSidebar: false,
  //   module: "MerchantGroup",
  //   subModule: "Browse",
  // },
  {
    type: "single",
    name: "Group Config",
    key: "group-config",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/group-config",
    component: (
      <ProtectedRoute>
        <GroupConfig />
      </ProtectedRoute>
    ),
    doShowInSidebar: true,
    // module: "Group Config",
    // subModule: "Browse",
  },
  {
    type: "single",
    name: "Device Config",
    key: "device-config",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/device-config",
    component: (
      <ProtectedRoute>
        <Deviceconfig />
      </ProtectedRoute>
    ),
    doShowInSidebar: true,
    // module: "Device Config",
    // subModule: "Browse",
  },
  {
    type: "single",
    name: "Device Config",
    key: "device-config",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/device-config/:groupId",
    component: (
      <ProtectedRoute>
        <Deviceconfig />
      </ProtectedRoute>
    ),
    doShowInSidebar: false,
    // module: "Device Config",
    // subModule: "Browse",
  },
  {
    type: "single",
    name: "Parameter Config",
    key: "parameter-config",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/parameter-config",
    component: (
      <ProtectedRoute>
        <Parameterconfig />
      </ProtectedRoute>
    ),
    doShowInSidebar: true,
    // module: "Parameter Config",
    // subModule: "Browse",
  },
  {
    type: "single",
    name: "Parameters",
    key: "control-config-view",
    icon: <Icon fontSize="small">rule</Icon>,
    route: "/control-config-view",
    component: (
      <ProtectedRoute>
        <AllControlConfig />
      </ProtectedRoute>
    ),
    doShowInSidebar: false,
    // module: "Control Config View",
    // subModule: "Browse",
  },
  {
    type: "single",
    name: "Deleted Devices",
    key: "deleted-device",
    icon: <Icon fontSize="small">delete</Icon>,
    route: "/deleted-devices",
    component: (
      <ProtectedRoute>
        <DeletedDevices />
      </ProtectedRoute>
    ),
    doShowInSidebar: true,
  },
  {
    type: "single",
    name: "RTL",
    key: "rtl",
    icon: <Icon fontSize="small">format_textdirection_r_to_l</Icon>,
    route: "/login",
    component: <SignIn />,
    doShowInSidebar: false,
  },
  {
    type: "single",
    name: "Reset Password",
    key: "reset-password",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/reset-password",
    component: (
      <ProtectedRoute>
        <ResetPassword />
      </ProtectedRoute>
    ),
    doShowInSidebar: false,
    module: "Authentication",
    subModule: "AuthResetPassword",
  },
  {
    type: "single",
    name: "Users",
    key: "users",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/users",
    component: (
      <ProtectedRoute>
        <Users />
      </ProtectedRoute>
    ),
    doShowInSidebar: true,
    module: "User",
    subModule: "BrowseUser",
  },
  // {
  //   type: "single",
  //   name: "Routing Rules",
  //   key: "routing-rules",
  //   icon: <Icon fontSize="small">rule</Icon>,
  //   route: "/routing-rules",
  //   component: <RoutingRules />,
  //   doShowInSidebar: true,
  //   module: "RoutingRule",
  //   subModule: "Browse",
  // },
  // {
  //   type: "collapse",
  //   name: "Reset Activity",
  //   key: "reset-activity",
  //   icon: <Icon fontSize="small">pin</Icon>,
  //   doShowInSidebar: true,
  //   collapse: [
  //     {
  //       type: "single",
  //       name: "Reset IMEI",
  //       key: "reset-imei",
  //       icon: <Icon fontSize="small">pin</Icon>,
  //       route: "/reset-imei",
  //       component: <ResetImei/>,
  //       doShowInSidebar: true,
  //       module: "Reset",
  //       subModule: "Imei",
  //     },
  //     {
  //       type: "single",
  //       name: "Reset PIN",
  //       key: "reset-pin",
  //       icon: <Icon fontSize="small">pin</Icon>,
  //       route: "/reset-pin",
  //       component: <ResetPin />,
  //       doShowInSidebar: true,
  //       module: "Reset",
  //       subModule: "AppPassword",
  //     },
  //   ]
  // },
  {
    type: "collapse",
    name: "Configs",
    key: "configs",
    icon: <Icon fontSize="small">cell_tower</Icon>,
    doShowInSidebar: true,
    collapse: [
      // {
      //   type: "single",
      //   name: "Debit Account Config",
      //   key: "system-configuration",
      //   icon: <Icon fontSize="small">rule</Icon>,
      //   route: "/system-config",
      //   component: <DebitAccountConfig />,
      //   doShowInSidebar: true,
      //   // module: "SystemConfiguration",
      //   // subModule: "Browse",
      // },
      {
        type: "single",
        name: "System Config",
        key: "system-config",
        route: "/system-config",
        component: (
          <ProtectedRoute>
            <DebitAccountConfig />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
      },
      {
        type: "single",
        name: "Merchant Category Code",
        key: "merchant-category-code",
        route: "/merchant-category-code",
        component: (
          <ProtectedRoute>
            <MerchantCategoryCodeConfig />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
      },
      {
        type: "single",
        name: "Bank TLS Config",
        key: "bank-tlsconfig",
        route: "bank-tlsconfig",
        component: (
          <ProtectedRoute>
            <BankTLSConfig />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
      },
      {
        type: "collapse",
        name: "MDR Master",
        key: "mdr-master",
        icon: <Icon fontSize="small">reports</Icon>,
        route: "/mdr-master",
        component: (
          <ProtectedRoute>
            <MdrMasterChannels />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
        // module: "Masters",
        // subModule: "BrowseMdrMaster",
      },
      // {
      //   type: "collapse",
      //   name: "MDR Default Master",
      //   key: "mdr-default-master",
      //   icon: <Icon fontSize="small">reports</Icon>,
      //   route: "/mdr-default-master",
      //   component: (
      //     <ProtectedRoute>
      //       <MdrDefaultMaster />
      //     </ProtectedRoute>
      //   ),
      //   doShowInSidebar: false,
      //   // module: "Masters",
      //   // subModule: "BrowseDefaultMdrMaster",
      // },
      // {
      //   type: "collapse",
      //   name: "MDR Master New",
      //   key: "mdr-master-new",
      //   icon: <Icon fontSize="small">reports</Icon>,
      //   route: "/mdr-master-new",
      //   component: (
      //     <ProtectedRoute>
      //       <MdrMasterNew />
      //     </ProtectedRoute>
      //   ),
      //   doShowInSidebar: true,
      //   // module: "Masters",
      //   // subModule: "BrowseMdrMaster",
      // },
      // {
      //   type: "collapse",
      //   name: "CAPK Master",
      //   key: "capk-master",
      //   icon: <Icon fontSize="small">reports</Icon>,
      //   route: "/capk-master",
      //   component: <ProtectedRoute><CapkMaster /></ProtectedRoute>,
      //   doShowInSidebar: true,
      //   module: "Masters",
      //   subModule: "BrowseBinMaster",
      // },
      // {
      //   type: "collapse",
      //   name: "Bin Master",
      //   key: "bin-master",
      //   icon: <Icon fontSize="small">reports</Icon>,
      //   route: "/bin-master",
      //   component: (
      //     <ProtectedRoute>
      //       <BinMaster />
      //     </ProtectedRoute>
      //   ),
      //   doShowInSidebar: true,
      //   // module: "Masters",
      //   // subModule: "BrowseBinMaster",
      // },
    ],
  },
  // {
  //   type: "collapse",
  //   name: "Track",
  //   key: "track",
  //   icon: <Icon fontSize="small">troubleshoot</Icon>,
  //   doShowInSidebar: true,
  //   collapse: [
  //     {
  //       type: "collapse",
  //       name: "Track Sms",
  //       key: "track-sms",
  //       icon: <Icon fontSize="small">reports</Icon>,
  //       route: "/track-sms",
  //       component: <ProtectedRoute><TrackSms /></ProtectedRoute>,
  //       doShowInSidebar: true,
  //       module: "Tracking",
  //       subModule: "Sms",
  //     },
  //     {
  //       type: "collapse",
  //       name: "Track Email",
  //       key: "track-email",
  //       icon: <Icon fontSize="small">reports</Icon>,
  //       route: "/track-email",
  //       component: <ProtectedRoute><TrackEmail /></ProtectedRoute>,
  //       doShowInSidebar: true,
  //       module: "Tracking",
  //       subModule: "Email",
  //     },
  //   ]
  // },
  {
    type: "single",
    name: "Merchants",
    key: "merchants",
    icon: <Icon fontSize="small">store</Icon>,
    route: "/merchants",
    component: (
      <ProtectedRoute>
        <Merchants />
      </ProtectedRoute>
    ),
    doShowInSidebar: true,
    // module: "Merchants",
    // subModule: "Browse",
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="small">receipt_long</Icon>,
    doShowInSidebar: true,
    collapse: [
      //     {
      //       type: "collapse",
      //       name: "Transaction Summary",
      //       key: "transaction-summary",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/transaction-summary",
      //       component: <ProtectedRoute><TransactionSummary /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "TransactionSummary",
      //     },
      {
        type: "collapse",
        name: "QR Transactions Report",
        key: "QR-transaction-report",
        icon: <Icon fontSize="small">reports</Icon>,
        route: "/QR-transaction-report",
        component: (
          <ProtectedRoute>
            <TransactionReport />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
        // module: "Reports",
        // subModule: "TotalTransactions",
      },
      //     {
      //       type: "collapse",
      //       name: "Active users",
      //       key: "active-users",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/active-users",
      //       component: <ProtectedRoute><ThirtyDaysActive /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "ActiveUsers",
      //     },
          {
            type: "collapse",
            name: "Card Transactions Report",
            key: "card-transactions",
            icon: <Icon fontSize="small">reports</Icon>,
            route: "/card-transactions",
            component: <ProtectedRoute><CardTransactions /></ProtectedRoute>,
            doShowInSidebar: true,
            // module: "Reports",
            // subModule: "CardTransactions",
          },
          {
            type: "collapse",
            name: "SoftPos Transaction Reports",
            key: "softpos-transaction-report",
            icon: <Icon fontSize="small">reports</Icon>,
            route: "/softpos-transaction-report",
            component: <ProtectedRoute><SoftPosTransactionReport /></ProtectedRoute>,
            doShowInSidebar: true,
            // module: "Reports",
            // subModule: "SoftPosTransactions",
          },
          // {
          //   type: "collapse",
          //   name: "Card Txns (Merchant/MDR)",
          //   key: "card-transactions-detailed",
          //   icon: <Icon fontSize="small">reports</Icon>,
          //   route: "/card-transactions-detailed",
          //   component: <ProtectedRoute><CardTransactionsDetailed /></ProtectedRoute>,
          //   doShowInSidebar: true,
          //   // module: "Reports",
          //   // subModule: "CardTransactions",
          // },
      //     {
      //       type: "collapse",
      //       name: "CNP Transactions",
      //       key: "cnp-transactions",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/cnp-transactions",
      //       component: <ProtectedRoute><CnpTransactions /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "CardTransactions",
      //     },
      //     {
      //       type: "collapse",
      //       name: "Refund Transactions",
      //       key: "refund-transactions",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/refund-transactions",
      //       component: <ProtectedRoute><RefundTransactions /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "RefundTransactions",
      //     },
      //     {
      //       type: "collapse",
      //       name: "Void Transactions",
      //       key: "void-transactions",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/void-transactions",
      //       component: <ProtectedRoute><VoidTransactions /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "VoidTransactions",
      //     },
      //     {
      //       type: "collapse",
      //       name: "Cash Transactions",
      //       key: "cash-transactions",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/cash-transactions",
      //       component: <ProtectedRoute><CashTransactions /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "CashTransactions",
      //     },
      //     {
      //       type: "collapse",
      //       name: "DWH Transactions",
      //       key: "dwh-card-transactions",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/dwh-card-transactions",
      //       component: <ProtectedRoute><DwhTransactions /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "DwhCardTransactions",
      //     },
      //     {
      //       type: "collapse",
      //       name: "Card Settlement Summary",
      //       key: "card-settlement-summary",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/card-settlement-summary",
      //       component: <ProtectedRoute><CardSettlementSummary /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "CardSettlementSummary",
      //     },
      //     {
      //       type: "collapse",
      //       name: "POS Transaction Report",
      //       key: "pos-transaction-report",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/pos-transaction-report",
      //       component: <ProtectedRoute><PosTransactionReport /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "PosTransactions",
      //     },
      {
        type: "collapse",
        name: "QR Settlement Report",
        key: "pos-settlement-report",
        icon: <Icon fontSize="small">reports</Icon>,
        route: "/pos-settlement-report",
        component: (
          <ProtectedRoute>
            <SettlementReport />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
        // module: "Reports",
        // subModule: "SettlementReport",
      },
      {
        type: "collapse",
        name: "Card Settlement Report",
        key: "card-settlement-report",
        icon: <Icon fontSize="small">reports</Icon>,
        route: "/card-settlement-report",
        component: (
          <ProtectedRoute>
            <CardSettlementReport />
          </ProtectedRoute>
        ),
        doShowInSidebar: true,
        // module: "Reports",
        // subModule: "CardSettlementReport",
      },
      //       {
      //           type: "collapse",
      //           name: "Merchant Statement",
      //           key: "merchant-statement",
      //           icon: <Icon fontSize="small">reports</Icon>,
      //           route: "/merchant-statement",
      //           component: <ProtectedRoute><MerchantStatement /></ProtectedRoute>,
      //           doShowInSidebar: true,
      //           module: "Reports",
      //           subModule: "MerchantStatement",
      //       },
      //     {
      //       type: "collapse",
      //       name: "Gross Settlement Report",
      //       key: "gross-settlement-report",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/gross-settlement-report",
      //       component: <ProtectedRoute><GrossSettlementReport /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "GrossSettlement",
      //     },
      //     {
      //       type: "collapse",
      //       name: "Net Settlement Report",
      //       key: "net-settlement-report",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/net-settlement-report",
      //       component: <ProtectedRoute><NetSettlementReport /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "NetSettlement",
      //     },
      //     {
      //       type: "collapse",
      //       name: "Net Settlement Report (Monthly)",
      //       key: "monthly-net-settlement-report",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/monthly-net-settlement-report",
      //       component: <ProtectedRoute><MonthlyNetSettlementReport /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "NetSettlement",
      //     },
      //     {
      //       type: "collapse",
      //       name: "DWH Proccess Release ",
      //       key: "dwh_file_proccess",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/dwh_file_proccess",
      //       component: <ProtectedRoute><DwhFilesProccess /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "DwhFilesProccess",
      //    },
      //     {
      //       type: "collapse",
      //       name: "DE FILE Release ",
      //       key: "de_file_release",
      //       icon: <Icon fontSize="small">reports</Icon>,
      //       route: "/de_file_release",
      //       component: <ProtectedRoute><DeFileReleaseScreen /></ProtectedRoute>,
      //       doShowInSidebar: true,
      //       module: "Reports",
      //       subModule: "DeFileReleaseScreen",
      //    },
      //    {
      //     type: "collapse",
      //     name: "DE Reply File Report ",
      //     key: "de_reply_file",
      //     icon: <Icon fontSize="small">reports</Icon>,
      //     route: "/de_reply_file",
      //     component: <ProtectedRoute><DeReplyFile /></ProtectedRoute>,
      //     doShowInSidebar: true,
      //     module: "Reports",
      //     subModule: "DeReplyFile",
      //  },
      //     // {
      //     //   type: "collapse",
      //     //   name: "DE FILE Release ",
      //     //   key: "de_file_release",
      //     //   icon: <Icon fontSize="small">reports</Icon>,
      //     //   route: "/de_file_release",
      //     //   component: <ProtectedRoute><DeFileReleaseScreen /></ProtectedRoute>,
      //     //   doShowInSidebar: true,
      //     //   module: "Reports",
      //     //   subModule: "NetSettlement",
      //     // }
    ],
  },
  {
    type: "single",
    name: "Forgot Password",
    key: "forgot-password",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/forgot-password",
    component: <Cover />,
    doShowInSidebar: false,
  },
  {
    type: "single",
    name: "Audit Trails",
    key: "audit-trails",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/audit-trails",
    component: <ProtectedRoute><Audittrails /></ProtectedRoute>,
    doShowInSidebar: true,
    // module: "Audit",
    // subModule: "Browse",
  },
  {
    type: "single",
    name: "Forgot Password",
    key: "first-password",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/first-password",
    component: <FirstTime />,
    doShowInSidebar: false,
  },
  {
    type: "single",
    name: "Reset Password",
    key: "reset",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/reset",
    component: <ResetWithOtp />,
    doShowInSidebar: false,
  },
  {
    type: "single",
    name: "New Merchant",
    key: "new_merchant",
    icon: <Icon fontSize="small">store</Icon>,
    route: "/merchants/new",
    component: (
      <ProtectedRoute>
        <NewMerchant />
      </ProtectedRoute>
    ),
    doShowInSidebar: false,
  },
  {
    type: "single",
    name: "Merchant",
    key: "merchant",
    icon: <Icon fontSize="small">store</Icon>,
    route: "/merchants/:mid",
    component: (
      <ProtectedRoute>
        <Single />
      </ProtectedRoute>
    ),
    doShowInSidebar: false,
  },
];

export default routes;
